<template>
  <div class="trip-review-view">
    <AppHeader />

    <main class="trip-review-main">
      <div class="container">
        <button class="back-button" type="button" @click="goBack">
          戻る
        </button>

        <section class="review-panel">
          <header class="review-header">
            <p class="review-kicker">次の旅行のヒントに</p>
            <h1>旅の振り返りから、次の旅行へ</h1>
            <p>みんなの好みを記録して、次の旅行の参考にしましょう。評価は任意です。</p>
            <span v-if="isReadOnly" class="review-read-only-badge">閲覧のみ</span>
          </header>

          <div v-if="tripReviewStore.loading" class="review-loading">
            <div class="spinner"></div>
            <p>読み込み中...</p>
          </div>

          <form v-else class="review-form" @submit.prevent="save">
            <section class="mobile-review-stepper" aria-label="振り返りステップ">
              <div class="step-progress">
                <div class="step-progress-copy">
                  <span>{{ currentStepLabel }}</span>
                  <strong>{{ currentStepIndex + 1 }} / {{ reviewSteps.length }}</strong>
                </div>
                <div class="step-progress-track" aria-hidden="true">
                  <span :style="{ width: `${reviewProgressPercent}%` }"></span>
                </div>
              </div>

              <section v-if="currentReviewStep?.type === 'overall'" class="mobile-step-card">
                <header class="mobile-step-header">
                  <p>STEP {{ currentStepIndex + 1 }}</p>
                  <h2>全体評価</h2>
                </header>
                <div class="score-list">
                  <div
                    v-for="category in categories"
                    :key="category.key"
                    class="score-row"
                  >
                    <div class="score-label">
                      <span>{{ category.label }}</span>
                      <small>{{ scoreText(form[category.key]) }}</small>
                    </div>
                    <div class="score-controls" role="group" :aria-label="category.label">
                      <button
                        v-for="score in scores"
                        :key="score"
                        type="button"
                        class="score-button"
                        :class="{ active: form[category.key] === score }"
                        :aria-label="scoreAriaLabel(category, score)"
                        :aria-pressed="form[category.key] === score"
                        :disabled="isReadOnly"
                        @click="toggleScore(category.key, score)"
                      >
                        {{ score }}
                      </button>
                      <button
                        type="button"
                        class="score-clear"
                        :disabled="isReadOnly || form[category.key] === null"
                        :aria-label="clearScoreAriaLabel(category)"
                        @click="form[category.key] = null"
                      >
                        未選択
                      </button>
                    </div>
                  </div>
                </div>
              </section>

              <section v-else-if="currentReviewStep?.item" class="mobile-step-card">
                <header class="mobile-step-header">
                  <p>STEP {{ currentStepIndex + 1 }}</p>
                  <h2>{{ currentStepLabel }}</h2>
                  <span>{{ currentReviewStep.item.itemName || currentReviewStep.item.lodgingName || "評価対象" }}</span>
                  <strong>{{ itemAverageScoreText(currentReviewStep.item) }}</strong>
                </header>
                <div class="item-score-list">
                  <div
                    v-for="scoreCategory in itemScoreCategories(currentReviewStep.item.reviewCategory)"
                    :key="`${currentReviewStep.item.lodgingKey}-${scoreCategory.key}`"
                    class="score-row item-score-row"
                  >
                    <div class="score-label">
                      <span>{{ scoreCategory.label }}</span>
                      <small>{{ scoreText(currentReviewStep.item[scoreCategory.key]) }}</small>
                    </div>
                    <div
                      class="score-controls"
                      role="group"
                      :aria-label="`${currentReviewStep.item.itemName || currentReviewStep.item.lodgingName || '評価対象'} ${scoreCategory.label}`"
                    >
                      <button
                        v-for="score in scores"
                        :key="score"
                        type="button"
                        class="score-button"
                        :class="{ active: currentReviewStep.item[scoreCategory.key] === score }"
                        :aria-label="scoreAriaLabel(scoreCategory, score)"
                        :aria-pressed="currentReviewStep.item[scoreCategory.key] === score"
                        :disabled="isReadOnly"
                        @click="toggleItemScore(currentReviewStep.item, scoreCategory.key, score)"
                      >
                        {{ score }}
                      </button>
                      <button
                        type="button"
                        class="score-clear"
                        :disabled="isReadOnly || currentReviewStep.item[scoreCategory.key] === null"
                        :aria-label="clearScoreAriaLabel(scoreCategory)"
                        @click="currentReviewStep.item[scoreCategory.key] = null"
                      >
                        未選択
                      </button>
                    </div>
                  </div>
                </div>
              </section>

              <div class="step-actions">
                <button
                  class="btn btn-secondary"
                  type="button"
                  :disabled="currentStepIndex === 0"
                  @click="previousStep"
                >
                  戻る
                </button>
                <button
                  v-if="!isLastReviewStep"
                  class="btn btn-primary"
                  type="button"
                  @click="nextStep"
                >
                  次へ進む
                </button>
                <button
                  v-else-if="canWrite"
                  class="btn btn-primary"
                  type="submit"
                  :disabled="tripReviewStore.saving"
                >
                  {{ tripReviewStore.saving ? "保存中..." : "評価を確定する" }}
                </button>
              </div>
            </section>

            <div class="score-list desktop-review-flow">
              <div
                v-for="category in categories"
                :key="category.key"
                class="score-row"
              >
                <div class="score-label">
                  <span>{{ category.label }}</span>
                  <small>{{ scoreText(form[category.key]) }}</small>
                </div>
                <div class="score-controls" role="group" :aria-label="category.label">
                  <button
                    v-for="score in scores"
                    :key="score"
                    type="button"
                    class="score-button"
                    :class="{ active: form[category.key] === score }"
                    :aria-label="scoreAriaLabel(category, score)"
                    :aria-pressed="form[category.key] === score"
                    :disabled="isReadOnly"
                    @click="toggleScore(category.key, score)"
                  >
                    {{ score }}
                  </button>
                  <button
                    type="button"
                    class="score-clear"
                    :disabled="isReadOnly || form[category.key] === null"
                    :aria-label="clearScoreAriaLabel(category)"
                    @click="form[category.key] = null"
                  >
                    未選択
                  </button>
                </div>
              </div>
            </div>

            <section
              v-if="groupedItemReviews.length"
              class="item-review-section desktop-review-flow"
            >
              <div class="item-review-heading">
                <h2>予定ごとの評価</h2>
                <p>宿・食事・移動・観光・アクティビティごとに、覚えておきたい粒度で残せます。</p>
              </div>

              <div
                v-for="group in groupedItemReviews"
                :key="group.key"
                class="item-review-group"
              >
                <h3>{{ group.label }}</h3>
                <article
                  v-for="item in group.items"
                  :key="item.lodgingKey"
                  class="item-review-card"
                >
                  <header class="item-review-card-header">
                    <div>
                      <h4>{{ item.itemName || item.lodgingName || "評価対象" }}</h4>
                      <p v-if="item.address">{{ item.address }}</p>
                      <p v-if="item.startDate">{{ formatDate(item.startDate) }}</p>
                    </div>
                    <a
                      v-if="item.url"
                      class="item-link"
                      :href="item.url"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      URL
                    </a>
                  </header>

                  <div class="item-score-list">
                    <div
                      v-for="scoreCategory in itemScoreCategories(item.reviewCategory)"
                      :key="`${item.lodgingKey}-${scoreCategory.key}`"
                      class="score-row item-score-row"
                    >
                      <div class="score-label">
                        <span>{{ scoreCategory.label }}</span>
                        <small>{{ scoreText(item[scoreCategory.key]) }}</small>
                      </div>
                      <div
                        class="score-controls"
                        role="group"
                        :aria-label="`${item.itemName || item.lodgingName || '評価対象'} ${scoreCategory.label}`"
                      >
                        <button
                          v-for="score in scores"
                          :key="score"
                          type="button"
                          class="score-button"
                          :class="{ active: item[scoreCategory.key] === score }"
                          :aria-label="scoreAriaLabel(scoreCategory, score)"
                          :aria-pressed="item[scoreCategory.key] === score"
                          :disabled="isReadOnly"
                          @click="toggleItemScore(item, scoreCategory.key, score)"
                        >
                          {{ score }}
                        </button>
                        <button
                          type="button"
                          class="score-clear"
                          :disabled="isReadOnly || item[scoreCategory.key] === null"
                          :aria-label="clearScoreAriaLabel(scoreCategory)"
                          @click="item[scoreCategory.key] = null"
                        >
                          未選択
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
            </section>

            <label class="free-text-field desktop-review-flow">
              <span>メモ</span>
              <textarea
                v-model="form.freeText"
                maxlength="2000"
                rows="7"
                placeholder="よかったこと、次回気をつけたいことなど"
                :readonly="isReadOnly"
              ></textarea>
              <small>{{ form.freeText.length }}/2000</small>
            </label>

            <label class="ai-consent desktop-review-flow">
              <input
                v-model="form.aiUsageConsent"
                type="checkbox"
                :disabled="isReadOnly"
              />
              <span>
                今後のAIスケジュール生成やおすすめ改善に、この振り返りを使うことに同意します。
              </span>
            </label>

            <p
              v-if="tripReviewStore.error"
              class="review-error"
              role="alert"
              aria-live="assertive"
            >
              {{ tripReviewStore.error }}
            </p>

            <div class="review-actions desktop-review-flow">
              <button class="btn btn-secondary" type="button" @click="goBack">
                キャンセルする
              </button>
              <button
                v-if="canWrite"
                class="btn btn-primary"
                type="submit"
                :disabled="tripReviewStore.saving"
              >
                {{ tripReviewStore.saving ? "保存中..." : "一時保存" }}
              </button>
            </div>
          </form>
        </section>
      </div>
    </main>
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import AppHeader from "@/components/AppHeader.vue";
import { useTripReviewStore } from "@/store/trip-review-store.js";
import { useShioriStore } from "@/store/shiori-store.js";

const categories = [
  { key: "lodgingScore", label: "充実さ" },
  { key: "foodScore", label: "時間効率" },
  { key: "transportScore", label: "コスパ" },
  { key: "sightseeingScore", label: "快適性" },
  { key: "activityScore", label: "新鮮さ" },
];

const scores = [1, 2, 3, 4, 5];

const categoryLabels = {
  lodging: "宿泊",
  food: "食事",
  transport: "移動",
  sightseeing: "観光",
  activity: "アクティビティ",
};

const stepCategoryLabels = {
  lodging: "ホテル",
  food: "食事",
  transport: "移動",
  sightseeing: "観光",
  activity: "アクティビティ",
};

const circledNumbers = ["①", "②", "③", "④", "⑤", "⑥", "⑦", "⑧", "⑨", "⑩"];

const itemCategoryLabels = {
  lodging: ["清潔さ", "設備・アメニティ", "サービスの質", "立地・アクセス", "コストパフォーマンス"],
  food: ["味", "雰囲気", "サービス", "立地・アクセス", "コストパフォーマンス"],
  transport: ["快適さ", "時間の正確さ", "わかりやすさ", "立地・アクセス", "コストパフォーマンス"],
  sightseeing: ["見応え", "混雑具合", "アクセス", "過ごしやすさ", "コストパフォーマンス"],
  activity: ["楽しさ", "安全性", "スタッフ対応", "アクセス", "コストパフォーマンス"],
};

const itemScoreKeys = [
  "cleanlinessScore",
  "facilitiesScore",
  "serviceScore",
  "locationScore",
  "valueScore",
];

export default {
  name: "TripReviewView",
  components: {
    AppHeader,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const tripReviewStore = useTripReviewStore();
    const shioriStore = useShioriStore();
    const itemReviews = ref([]);
    const currentStepIndex = ref(0);
    const form = reactive({
      lodgingScore: null,
      foodScore: null,
      transportScore: null,
      sightseeingScore: null,
      activityScore: null,
      freeText: "",
      aiUsageConsent: false,
    });

    const shioriId = route.params.shioriId;
    const access = computed(
      () =>
        shioriStore.accessByShioriId[shioriId] || {
          canRead: false,
          canWrite: false,
        },
    );
    const canWrite = computed(() => access.value.canWrite);
    const isReadOnly = computed(
      () => access.value.canRead && !access.value.canWrite,
    );

    const itemScoreCategories = (reviewCategory) => {
      const labels = itemCategoryLabels[reviewCategory] || itemCategoryLabels.lodging;
      return itemScoreKeys.map((key, index) => ({ key, label: labels[index] }));
    };

    const groupedItemReviews = computed(() =>
      ["lodging", "food", "transport", "sightseeing", "activity"]
        .map((key) => ({
          key,
          label: categoryLabels[key],
          items: itemReviews.value.filter(
            (item) => (item.reviewCategory || "lodging") === key,
          ),
        }))
        .filter((group) => group.items.length > 0),
    );

    const reviewSteps = computed(() => {
      const categoryCounts = {};
      const itemSteps = itemReviews.value.map((item) => {
        const category = item.reviewCategory || "lodging";
        categoryCounts[category] = (categoryCounts[category] || 0) + 1;
        const label = stepCategoryLabels[category] || "予定";
        const indexLabel =
          circledNumbers[categoryCounts[category] - 1] || categoryCounts[category];
        return {
          type: "item",
          label: `${label}${indexLabel}`,
          item,
        };
      });

      return [{ type: "overall", label: "全体評価" }, ...itemSteps];
    });

    const currentReviewStep = computed(
      () => reviewSteps.value[currentStepIndex.value] || reviewSteps.value[0],
    );

    const currentStepLabel = computed(() => currentReviewStep.value?.label || "全体評価");

    const reviewProgressPercent = computed(() => {
      if (!reviewSteps.value.length) return 100;
      return Math.round(((currentStepIndex.value + 1) / reviewSteps.value.length) * 100);
    });

    const isLastReviewStep = computed(
      () => currentStepIndex.value >= reviewSteps.value.length - 1,
    );

    const scrollToPageTop = () => {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    };

    const nextStep = () => {
      if (!isLastReviewStep.value) {
        currentStepIndex.value += 1;
        scrollToPageTop();
      }
    };

    const previousStep = () => {
      if (currentStepIndex.value > 0) {
        currentStepIndex.value -= 1;
      }
    };

    const applyReview = (review) => {
      categories.forEach((category) => {
        form[category.key] = review?.[category.key] ?? null;
      });
      form.freeText = review?.freeText || "";
      form.aiUsageConsent = Boolean(review?.aiUsageConsent);
      itemReviews.value = tripReviewStore.itemReviews.map((item) => ({ ...item }));
      currentStepIndex.value = 0;
    };

    const toggleScore = (key, score) => {
      if (!canWrite.value) return;
      form[key] = form[key] === score ? null : score;
    };

    const toggleItemScore = (item, key, score) => {
      if (!canWrite.value) return;
      item[key] = item[key] === score ? null : score;
    };

    const formatDate = (value) => {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return `${date.getFullYear()}年${String(date.getMonth() + 1).padStart(
        2,
        "0",
      )}月${String(date.getDate()).padStart(2, "0")}日`;
    };

    const scoreText = (score) => (score ? `${score}/5` : "未選択");

    const itemAverageScoreText = (item) => {
      const values = itemScoreKeys
        .map((key) => item[key])
        .filter((value) => value !== null && value !== undefined);
      if (!values.length) return "未選択";
      const average = values.reduce((sum, value) => sum + Number(value), 0) / values.length;
      return `${average.toFixed(1)}/5`;
    };

    const scoreAriaLabel = (category, score) =>
      `${category.label}を${score}点にする`;
    const clearScoreAriaLabel = (category) =>
      `${category.label}の評価を未選択にする`;

    const goBack = () => {
      router.push(`/shiori/${shioriId}`);
    };

    const save = async () => {
      if (!canWrite.value) return;
      await tripReviewStore.saveReview(shioriId, {
        ...form,
        itemReviews: itemReviews.value,
      });
      alert("評価を保存しました。\nこのグループの旅行の好みが更新されました。");
      router.push(`/shiori/${shioriId}`);
    };

    const removeRevokedReview = async () => {
      tripReviewStore.clearShioriReviewData(shioriId);
      itemReviews.value = [];
      shioriStore.removeShioriFromMemory(shioriId);
      await router.replace("/mypage");
    };

    const refreshAccessAfterDenial = async (event) => {
      if (String(event.detail?.shioriId) !== String(shioriId)) return;
      try {
        await shioriStore.fetchShioriAccess(shioriId, { force: true });
      } catch (error) {
        if ([403, 404].includes(error.response?.status)) {
          await removeRevokedReview();
        }
      }
    };

    onMounted(async () => {
      window.addEventListener(
        "rabiitrip:shiori-access-denied",
        refreshAccessAfterDenial,
      );
      try {
        await shioriStore.fetchShioriAccess(shioriId, { force: true });
        const review = await tripReviewStore.fetchReview(shioriId);
        applyReview(review);
      } catch (error) {
        if ([403, 404].includes(error.response?.status)) {
          await removeRevokedReview();
        }
      }
    });

    onUnmounted(() => {
      window.removeEventListener(
        "rabiitrip:shiori-access-denied",
        refreshAccessAfterDenial,
      );
    });

    return {
      categories,
      scores,
      form,
      itemReviews,
      groupedItemReviews,
      reviewSteps,
      currentReviewStep,
      currentStepIndex,
      currentStepLabel,
      reviewProgressPercent,
      isLastReviewStep,
      tripReviewStore,
      canWrite,
      isReadOnly,
      toggleScore,
      toggleItemScore,
      itemScoreCategories,
      scoreText,
      itemAverageScoreText,
      formatDate,
      scoreAriaLabel,
      clearScoreAriaLabel,
      nextStep,
      previousStep,
      goBack,
      save,
    };
  },
};
</script>

<style scoped>
.trip-review-view {
  min-height: 100vh;
  background: var(--color-background);
  --review-navy: #EEF4FA;
  --review-navy-dark: #0F2742;
  --review-navy-soft: #F6FAFE;
  --review-navy-line: #B8C7D8;
}

.trip-review-main {
  padding: 1rem 0.75rem 2.5rem;
}

.back-button {
  display: inline-flex;
  align-items: center;
  margin-bottom: 1rem;
  min-height: 44px;
  padding: 0.5rem 0.75rem;
  color: var(--color-text-muted);
  background: transparent;
  border: none;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.back-button:hover {
  color: var(--review-navy-dark);
}

.back-button:focus-visible,
.score-button:focus-visible,
.score-clear:focus-visible,
.item-link:focus-visible,
.btn:focus-visible {
  outline: 3px solid var(--review-navy-line);
  outline-offset: 3px;
}

.review-panel {
  max-width: 860px;
  margin: 0 auto;
  padding: 1rem;
  background: white;
  border: 1px solid var(--review-navy-line);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.review-header {
  display: grid;
  gap: 0.4rem;
  margin-bottom: 1.25rem;
}

.review-kicker {
  margin: 0;
  color: var(--review-navy-dark);
  font-size: 0.8rem;
  font-weight: 800;
}

.review-header h1,
.item-review-heading h2,
.item-review-group h3,
.item-review-card-header h4 {
  margin: 0;
  color: var(--color-text);
}

.review-header h1 {
  font-size: 1.4rem;
  line-height: 1.35;
}

.review-header p,
.item-review-heading p {
  margin: 0;
  color: var(--color-text-muted);
  font-size: 0.9rem;
}

.review-read-only-badge {
  justify-self: start;
  padding: 0.3rem 0.7rem;
  border: 1px solid var(--review-navy-line);
  border-radius: 999px;
  background: var(--review-navy);
  color: var(--review-navy-dark);
  font-size: 0.78rem;
  font-weight: 800;
}

.review-loading {
  display: grid;
  justify-items: center;
  gap: 0.75rem;
  padding: 3rem 1rem;
  color: var(--color-text-muted);
}

.review-form,
.score-list,
.item-review-section,
.item-review-group,
.item-score-list {
  display: grid;
  gap: 1rem;
}

.desktop-review-flow {
  display: none;
}

.free-text-field.desktop-review-flow,
.ai-consent.desktop-review-flow {
  display: none;
}

.mobile-review-stepper {
  display: grid;
  gap: 1rem;
}

.step-progress {
  display: grid;
  gap: 0.55rem;
  padding: 0.875rem;
  background: var(--review-navy-soft);
  border: 1px solid var(--review-navy-line);
  border-radius: var(--radius-md);
}

.step-progress-copy {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  color: var(--review-navy-dark);
  font-size: 0.85rem;
  font-weight: 800;
}

.step-progress-track {
  height: 8px;
  overflow: hidden;
  background: white;
  border-radius: 999px;
}

.step-progress-track span {
  display: block;
  height: 100%;
  background: #0F2742;
  border-radius: inherit;
  transition: width 0.2s ease;
}

.mobile-step-card {
  display: grid;
  gap: 1rem;
}

.mobile-step-header {
  display: grid;
  gap: 0.25rem;
}

.mobile-step-header p,
.mobile-step-header h2,
.mobile-step-header span,
.mobile-step-header strong {
  margin: 0;
}

.mobile-step-header p {
  color: var(--review-navy-dark);
  font-size: 0.75rem;
  font-weight: 800;
}

.mobile-step-header h2 {
  color: var(--color-text);
  font-size: 1.1rem;
  line-height: 1.35;
}

.mobile-step-header span {
  color: var(--color-text-muted);
  font-size: 0.85rem;
  line-height: 1.45;
}

.mobile-step-header strong {
  justify-self: start;
  padding: 0.25rem 0.55rem;
  color: #0F2742;
  background: var(--review-navy);
  border: 1px solid var(--review-navy-line);
  border-radius: 999px;
  font-size: 0.82rem;
}

.step-actions {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 0.75rem;
}

.score-row {
  display: grid;
  gap: 0.75rem;
  padding: 0.875rem;
  background: var(--review-navy);
  border: 1px solid var(--review-navy-line);
  border-radius: var(--radius-md);
}

.score-label {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  color: var(--color-text);
  font-weight: 800;
}

.score-label small {
  color: var(--color-text-muted);
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}

.score-controls {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr)) minmax(4.25rem, 1.35fr);
  gap: 0.3rem;
}

.score-button,
.score-clear {
  width: 100%;
  min-height: 44px;
  padding: 0 0.35rem;
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text);
  font: inherit;
  font-weight: 800;
  white-space: nowrap;
  cursor: pointer;
}

.score-button.active {
  background: #0F2742;
  border-color: #0F2742;
  box-shadow: 0 0 0 3px rgba(15, 39, 66, 0.24);
  color: white;
}

.score-button:hover,
.score-clear:hover:not(:disabled) {
  border-color: var(--review-navy-dark);
}

.score-clear {
  color: var(--color-text-muted);
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}

.score-clear:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.item-review-heading {
  display: grid;
  gap: 0.25rem;
}

.item-review-group h3 {
  font-size: 1rem;
}

.item-review-card {
  display: grid;
  gap: 0.85rem;
  padding: 0.875rem;
  background: white;
  border: 1px solid var(--review-navy-line);
  border-radius: var(--radius-md);
}

.item-review-card-header {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  justify-content: space-between;
}

.item-review-card-header h4 {
  font-size: 1rem;
  line-height: 1.4;
}

.item-review-card-header p {
  margin: 0.2rem 0 0;
  color: var(--color-text-muted);
  font-size: 0.8rem;
  line-height: 1.4;
}

.item-link {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--review-navy-dark);
  font-size: 0.8rem;
  font-weight: 800;
  text-decoration: none;
}

.item-score-row {
  padding: 0.75rem;
}

.free-text-field {
  display: grid;
  gap: 0.5rem;
  color: var(--color-text);
  font-weight: 800;
}

.free-text-field textarea {
  width: 100%;
  padding: 0.875rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text);
  font: inherit;
  line-height: 1.6;
  resize: vertical;
}

.free-text-field textarea:focus {
  outline: none;
  border-color: var(--review-navy-dark);
  box-shadow: 0 0 0 3px var(--review-navy-soft);
}

.free-text-field small {
  justify-self: end;
  color: var(--color-text-muted);
  font-size: 0.75rem;
  font-weight: 700;
}

.ai-consent {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0.75rem;
  align-items: start;
  padding: 0.875rem;
  background: var(--review-navy);
  border: 1px solid var(--review-navy-line);
  border-radius: var(--radius-md);
  color: var(--color-text);
  font-size: 0.9rem;
  line-height: 1.55;
}

.ai-consent input {
  width: 18px;
  height: 18px;
  margin-top: 0.15rem;
  accent-color: var(--review-navy-dark);
}

.review-error {
  margin: 0;
  padding: 0.75rem 1rem;
  color: #991b1b;
  background: #fee2e2;
  border-radius: var(--radius-md);
  font-size: 0.875rem;
  font-weight: 700;
}

.review-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.75rem;
}

.step-actions .btn-primary,
.review-actions .btn-primary {
  color: var(--review-navy-dark);
  background: var(--review-navy);
  border-color: var(--review-navy-line);
}

.step-actions .btn-primary:hover:not(:disabled),
.review-actions .btn-primary:hover:not(:disabled) {
  color: white;
  background: var(--review-navy-dark);
  border-color: var(--review-navy-dark);
}

.step-actions .btn-secondary,
.review-actions .btn-secondary {
  color: var(--review-navy-dark);
  background: white;
  border-color: var(--review-navy-line);
}

.step-actions .btn-secondary:hover:not(:disabled),
.review-actions .btn-secondary:hover {
  background: var(--review-navy-soft);
  border-color: var(--review-navy-dark);
}

@media (min-width: 768px) {
  .trip-review-main {
    padding: 2rem 1rem 3.5rem;
  }

  .review-panel {
    padding: 1.5rem;
  }

  .review-header h1 {
    font-size: 1.75rem;
  }

  .mobile-review-stepper {
    display: none;
  }

  .desktop-review-flow {
    display: grid;
  }

  .free-text-field.desktop-review-flow,
  .ai-consent.desktop-review-flow {
    display: grid;
  }

  .review-actions.desktop-review-flow {
    display: flex;
  }

  .score-row {
    grid-template-columns: minmax(140px, 1fr) auto;
    align-items: center;
  }

  .score-controls {
    display: flex;
    flex-wrap: wrap;
  }

  .score-button,
  .score-clear {
    width: auto;
    min-width: 44px;
  }
}
</style>
